import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReplayIcon from "@mui/icons-material/Replay";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StorageIcon from "@mui/icons-material/Storage";
import LayersIcon from "@mui/icons-material/Layers";
import { resolveAssistPageContext } from "../../../helpers/assistPageContext";
import { logAskTurn, sendAskMessage } from "../../../api/ask";
import { useAsk } from "../../../helpers/AskContext";
import {
  ASK_SRC,
  pageTitleOf,
  readLateOrdersDays,
  scrubAskText,
  trackAsk,
} from "../../../helpers/ask";
import {
  looksLikeHandoffQuestion,
  resolveAskQuestion,
  suggestionsForPage,
} from "../../../helpers/askAnswers";
import { AskMark } from "../../Ask/AskMark";
import { AskRichText } from "../../Ask/AskRichText";
import "./AskPanel.scss";

const SRC_ICONS = {
  shop: StorageIcon,
  help: MenuBookIcon,
  page: LayersIcon,
};

const Receipt = ({ steps, isPlatformAdmin, onExpand }) => {
  const [open, setOpen] = useState(false);
  const kinds = [...new Set((steps || []).map((s) => s.src).filter(Boolean))];

  return (
    <div className="riverr-ask__receipt">
      <button
        type="button"
        className="riverr-ask__receipt-btn"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) onExpand?.();
        }}
        aria-expanded={open}
      >
        {open ? (
          <ExpandMoreIcon sx={{ fontSize: 12 }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 12 }} />
        )}
        <span className="riverr-ask__receipt-kinds">
          {kinds.map((k) => {
            const Icon = SRC_ICONS[k] || StorageIcon;
            return (
              <span key={k} className="riverr-ask__receipt-kind">
                <Icon sx={{ fontSize: 11 }} />
                <span>{ASK_SRC[k]?.label || k}</span>
              </span>
            );
          })}
          <span>· {(steps || []).length} checked</span>
        </span>
      </button>
      {open && (
        <div className="riverr-ask__receipt-list">
          {(steps || []).map((step, i) => {
            const Icon = SRC_ICONS[step.src] || StorageIcon;
            return (
              <div key={`${step.src}-${i}`} className="riverr-ask__receipt-row">
                <Icon
                  sx={{ fontSize: 11, color: "#0F6B63", marginTop: "3px" }}
                />
                <span className="riverr-ask__source-tag">
                  {ASK_SRC[step.src]?.label || step.src}
                </span>
                <span className="riverr-ask__receipt-label">
                  {step.label}
                  {isPlatformAdmin && step.call ? (
                    <span className="riverr-ask__receipt-call">{step.call}</span>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ResultRows = ({ rows, rowsFrom, rowsMore, onRow }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? rows : rows.slice(0, 5);
  const hidden = showAll ? 0 : rowsMore || Math.max(0, rows.length - 5);

  return (
    <div className="riverr-ask__rows">
      {rowsFrom ? (
        <div className="riverr-ask__rows-from">from {rowsFrom}</div>
      ) : null}
      {visible.map((row) => (
        <button
          key={row.id}
          type="button"
          className="riverr-ask__row"
          onClick={() => onRow(row)}
        >
          <span className={`riverr-ask__dot riverr-ask__dot--${row.flag || "none"}`} />
          <span className="riverr-ask__row-main">
            <span className="riverr-ask__row-top">
              <span className="riverr-ask__row-id">{row.id}</span>
              <span className="riverr-ask__row-label">{row.label}</span>
            </span>
            {row.note ? (
              <span className="riverr-ask__row-note">{row.note}</span>
            ) : null}
          </span>
          {row.meta ? (
            <span className="riverr-ask__row-meta">{row.meta}</span>
          ) : null}
          <OpenInNewIcon sx={{ fontSize: 12, color: "#A8B0BA" }} />
        </button>
      ))}
      {hidden > 0 ? (
        <div className="riverr-ask__more">
          <button
            type="button"
            className="riverr-ask__more-btn"
            onClick={() => setShowAll(true)}
          >
            Show {hidden} more
          </button>
        </div>
      ) : null}
    </div>
  );
};

const DocCard = ({ doc, onOpen }) => (
  <button type="button" className="riverr-ask__doc" onClick={() => onOpen(doc)}>
    <MenuBookIcon sx={{ fontSize: 16, color: "#0F6B63" }} />
    <span className="min-w-0 flex-1" style={{ minWidth: 0, flex: 1 }}>
      <span className="riverr-ask__doc-title">{doc.title}</span>
      <span className="riverr-ask__doc-meta">help center</span>
    </span>
    <OpenInNewIcon sx={{ fontSize: 12, color: "#A8B0BA" }} />
  </button>
);

const AssistantTurn = ({
  msg,
  isLast,
  isPlatformAdmin,
  onAction,
  onRow,
  onDoc,
  onCopy,
  onRetry,
}) => (
  <div className="riverr-ask__turn">
    <div className="riverr-ask__turn-row">
      <div className="riverr-ask__gutter">
        <AskMark size={13} />
        <div className="riverr-ask__spine" />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        {(msg.body || []).map((p, i) => (
          <p key={i} className="riverr-ask__body">
            <AskRichText>{scrubAskText(p)}</AskRichText>
          </p>
        ))}
        {msg.rows?.length ? (
          <ResultRows
            rows={msg.rows}
            rowsFrom={msg.rowsFrom}
            rowsMore={msg.rowsMore}
            onRow={onRow}
          />
        ) : null}
        {msg.docLink ? <DocCard doc={msg.docLink} onOpen={onDoc} /> : null}
        <Receipt
          steps={msg.steps}
          isPlatformAdmin={isPlatformAdmin}
          onExpand={() => trackAsk("ask_receipt_expanded")}
        />
        {msg.actions?.length ? (
          <div className="riverr-ask__actions">
            {msg.actions.slice(0, 3).map((action, i) => (
              <button
                key={`${action.label}-${i}`}
                type="button"
                className={`riverr-ask__action ${
                  i === 0
                    ? "riverr-ask__action--primary"
                    : "riverr-ask__action--secondary"
                }`}
                onClick={() => onAction(action)}
              >
                {action.label}
              </button>
            ))}
          </div>
        ) : null}
        {isLast ? (
          <div className="riverr-ask__turn-tools">
            <button type="button" className="riverr-ask__ghost" onClick={onCopy}>
              <ContentCopyIcon sx={{ fontSize: 12 }} /> Copy
            </button>
            <button type="button" className="riverr-ask__ghost" onClick={onRetry}>
              <ReplayIcon sx={{ fontSize: 12 }} /> Retry
            </button>
          </div>
        ) : null}
      </div>
    </div>
  </div>
);

const Working = ({ steps }) => (
  <div className="riverr-ask__turn">
    <div className="riverr-ask__turn-row">
      <div className="riverr-ask__gutter">
        <AskMark size={13} color="#A8B0BA" />
      </div>
      <div className="riverr-ask__working">
        {(steps || []).map((step, i) => {
          const Icon = SRC_ICONS[step.src] || StorageIcon;
          const last = i === steps.length - 1;
          return (
            <div key={`${step.label}-${i}`} className="riverr-ask__step">
              {last ? (
                <Icon
                  sx={{ fontSize: 11, color: "#0F6B63", marginTop: "3px" }}
                  className="riverr-ask__pulse"
                />
              ) : (
                <CheckIcon
                  sx={{ fontSize: 11, color: "#0F6B63", marginTop: "3px" }}
                />
              )}
              <span className="riverr-ask__source-tag">
                {ASK_SRC[step.src]?.label || step.src}
              </span>
              <span
                className={`riverr-ask__step-label${
                  last ? "" : " riverr-ask__step-label--done"
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

const AskPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state?.currentUser?.user || {});
  const ask = useAsk();
  const {
    enabled,
    open,
    wide,
    overlay,
    shopName,
    isPlatformAdmin,
    threadKey,
    composerRef,
    closeAsk,
    setWide,
    newConversation,
    consumePrefill,
    consumeAutoSend,
    pin,
  } = ask || {};

  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [working, setWorking] = useState(false);
  const [steps, setSteps] = useState([]);
  const [usePageContext, setUsePageContext] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [liveText, setLiveText] = useState("");
  const [error, setError] = useState(null);
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const listRef = useRef(null);
  const panelRef = useRef(null);
  const abortRef = useRef(null);
  const conversationRef = useRef(null);
  const lastQuestionRef = useRef("");
  const stepsRef = useRef([]);
  const pageContext = resolveAssistPageContext(location.pathname);
  const pageTitle = pin?.label || pageTitleOf(pageContext);
  const suggestions = useMemo(
    () => suggestionsForPage(pageContext.route || location.pathname),
    [pageContext.route, location.pathname]
  );

  useEffect(() => {
    setItems([]);
    setDraft("");
    setSteps([]);
    setWorking(false);
    setUsePageContext(true);
    setConfirm(null);
    setError(null);
    conversationRef.current = null;
    const q = consumePrefill?.();
    const send = consumeAutoSend?.();
    if (q && send) setPendingQuestion({ text: q });
    else if (q) setDraft(q);
  }, [threadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const q = consumePrefill?.();
    const send = consumeAutoSend?.();
    if (q && send) setPendingQuestion({ text: q });
    else if (q) setDraft(q);
  }, [open, consumePrefill, consumeAutoSend]);

  const scrollLatest = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollLatest();
  }, [items, steps, working, scrollLatest]);

  useEffect(() => {
    if (!open || !overlay) return undefined;
    const root = panelRef.current;
    if (!root) return undefined;
    const focusables = () =>
      Array.from(
        root.querySelectorAll(
          'button, [href], textarea, input, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((n) => !n.hasAttribute("disabled"));
    const first = focusables()[0];
    first?.focus();
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const nodes = focusables();
      if (!nodes.length) return;
      const i = nodes.indexOf(document.activeElement);
      if (e.shiftKey && (i <= 0)) {
        e.preventDefault();
        nodes[nodes.length - 1].focus();
      } else if (!e.shiftKey && i === nodes.length - 1) {
        e.preventDefault();
        nodes[0].focus();
      }
    };
    root.addEventListener("keydown", onKey);
    return () => root.removeEventListener("keydown", onKey);
  }, [open, overlay]);

  const runQuestion = useCallback(
    async (text, opts = {}) => {
      const message = String(text || "").trim();
      if (!message || working) return;
      lastQuestionRef.current = message;
      setDraft("");
      setError(null);
      setItems((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: "user", text: message },
      ]);
      setSteps([]);
      stepsRef.current = [];
      setLiveText("");
      setWorking(true);

      try {
        const local = await resolveAskQuestion(message, {
          kind: opts.kind,
          user: currentUser,
          pageContext: pin
            ? {
                route: pin.route || pageContext.route,
                entity_type: pin.entity_type,
                entity_id: pin.entity_id,
              }
            : pageContext,
          transcript: [
            ...items
              .filter((row) => row.role === "user" || row.role === "assistant")
              .map((row) => ({
                role: row.role,
                kind: row.kind,
                content:
                  row.text ||
                  (Array.isArray(row.body) ? row.body.join("\n") : ""),
              })),
            { role: "user", content: message },
          ],
        });
        if (local) {
          setItems((prev) => [
            ...prev,
            { id: `a-${Date.now()}`, role: "assistant", ...local },
          ]);
          trackAsk("ask_answer_shown", {
            src_types: [
              ...new Set((local.steps || []).map((s) => s.src).filter(Boolean)),
            ],
            row_count: local.rows?.length || 0,
            has_doc_link: !!local.docLink,
          });
          logAskTurn({
            conversationId: conversationRef.current,
            question: message,
            body: local.body,
            kind: opts.kind || local.kind,
            pageContext: pin
              ? {
                  route: pin.route || pageContext.route,
                  entity_type: pin.entity_type,
                  entity_id: pin.entity_id,
                }
              : pageContext,
            answered: true,
          })
            .then((id) => {
              if (id) conversationRef.current = id;
            })
            .catch(() => {});
          setWorking(false);
          return;
        }
      } catch (_) {
        if (opts.kind === "handoff" || looksLikeHandoffQuestion(message)) {
          setError("Couldn't reach the team. Try again.");
          setWorking(false);
          return;
        }
      }

      abortRef.current?.abort();
      const abort = new AbortController();
      abortRef.current = abort;

      const ctx = pin
        ? {
            route: pin.route || pageContext.route,
            entity_type: pin.entity_type,
            entity_id: pin.entity_id,
          }
        : pageContext;

      let assembled = "";
      let gotResponse = false;
      const commitFallback = (id) => {
        const body = assembled
          .split(/\n\s*\n/)
          .map((p) => p.replace(/\s+/g, " ").trim())
          .filter(Boolean)
          .slice(0, 3);
        if (!body.length) return;
        setItems((prev) => [
          ...prev,
          {
            id: id || `a-${Date.now()}`,
            role: "assistant",
            body,
            steps: stepsRef.current.length
              ? stepsRef.current
              : [{ src: "shop", label: "Your shop" }],
          },
        ]);
        setLiveText(body.join(" "));
        trackAsk("ask_answer_shown", {
          src_types: ["shop"],
          row_count: 0,
          has_doc_link: false,
        });
      };

      try {
        await sendAskMessage({
          message,
          pageContext: ctx,
          usePageContext: usePageContext && !pin ? true : !!pin || usePageContext,
          conversationId: conversationRef.current,
          lateDays: readLateOrdersDays(),
          signal: abort.signal,
          onEvent: (event) => {
            if (event?.type === "conversation" && event.conversation_id) {
              conversationRef.current = event.conversation_id;
            } else if (event?.type === "step" && event.step) {
              stepsRef.current = [...stepsRef.current, event.step];
              setSteps(stepsRef.current);
            } else if (event?.type === "text" && event.delta) {
              assembled += event.delta;
              setLiveText(assembled);
            } else if (event?.type === "response" && event.response) {
              gotResponse = true;
              const response = {
                ...event.response,
                body: (event.response.body || []).map(scrubAskText),
              };
              setItems((prev) => [
                ...prev,
                {
                  id: `a-${Date.now()}`,
                  role: "assistant",
                  ...response,
                },
              ]);
              setLiveText((response.body || []).join(" "));
              trackAsk("ask_answer_shown", {
                src_types: [
                  ...new Set((response.steps || []).map((s) => s.src)),
                ],
                row_count: response.rows?.length || 0,
                has_doc_link: !!response.docLink,
              });
            } else if (event?.type === "done") {
              if (!gotResponse && assembled) {
                commitFallback(event.messageId);
                gotResponse = true;
              }
            } else if (event?.type === "tool_error") {
              setError(`Couldn't reach ${event.tool || "your shop"}. Try again.`);
            } else if (event?.type === "error") {
              setError("Couldn't reach your shop. Try again.");
            }
          },
        });
        if (!gotResponse && assembled) commitFallback();
        if (!gotResponse && !assembled) {
          setError("Couldn't reach your shop. Try again.");
        }
      } catch (err) {
        if (err?.name !== "AbortError") {
          setError("Couldn't reach your shop. Try again.");
        }
      } finally {
        setWorking(false);
      }
    },
    [working, pageContext, usePageContext, pin, currentUser, items]
  );

  useEffect(() => {
    if (!pendingQuestion?.text) return;
    const next = pendingQuestion;
    setPendingQuestion(null);
    trackAsk("ask_question_sent", { had_page_context: true });
    runQuestion(next.text, { kind: next.kind });
  }, [pendingQuestion, runQuestion]);

  const send = useCallback(() => {
    const message = draft.trim();
    if (!message) return;
    trackAsk("ask_question_sent", { had_page_context: usePageContext });
    runQuestion(message);
  }, [draft, usePageContext, runQuestion]);

  const goHref = (href) => {
    if (!href) return;
    if (/^https?:\/\//i.test(href)) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(href);
  };

  const runAction = (action) => {
    trackAsk("ask_action_clicked", {
      kind: action.kind,
      label: action.label,
    });
    if (action.kind === "mutate") {
      setConfirm(action);
      return;
    }
    if (action.kind === "external") {
      window.open(action.target, "_blank", "noopener,noreferrer");
      return;
    }
    goHref(action.target);
  };

  const confirmMutate = () => {
    const action = confirm;
    setConfirm(null);
    if (!action) return;
    const verb = String(action.label || "Updated").replace(/\.$/, "");
    setItems((prev) => [
      ...prev,
      {
        id: `sys-${Date.now()}`,
        role: "system",
        text: `${verb}. Records updated.`,
      },
    ]);
  };

  const empty = items.length === 0 && !working;

  if (!enabled || !open) return null;

  const mobile = overlay && typeof window !== "undefined" && window.innerWidth < 720;

  return (
    <>
      {overlay ? (
        <div
          className="riverr-ask__scrim"
          onClick={closeAsk}
          aria-hidden
        />
      ) : null}
      <aside
        ref={panelRef}
        className={`riverr-ask${overlay ? " riverr-ask--overlay" : ""}${
          mobile ? " riverr-ask--mobile" : ""
        }`}
        role="dialog"
        aria-label="Ask"
        aria-modal={overlay ? "true" : undefined}
      >
        <div className="riverr-ask__header">
          <AskMark size={15} />
          <span className="riverr-ask__title">Ask</span>
          <span className="riverr-ask__rule" />
          <span className="riverr-ask__shop">{shopName || "Your shop"}</span>
          <div className="riverr-ask__icons">
            <button
              type="button"
              className="riverr-ask__icon-btn"
              aria-label="New conversation"
              title="New conversation"
              onClick={newConversation}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </button>
            <button
              type="button"
              className="riverr-ask__icon-btn"
              aria-label={wide ? "Narrow" : "Widen"}
              title={wide ? "Narrow" : "Widen"}
              onClick={() => setWide(!wide)}
            >
              <ViewSidebarIcon sx={{ fontSize: 16 }} />
            </button>
            <button
              type="button"
              className="riverr-ask__exit"
              aria-label="Exit Ask"
              onClick={closeAsk}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
              Exit
            </button>
          </div>
        </div>

        {usePageContext ? (
          <div className="riverr-ask__hint">
            <LayersIcon sx={{ fontSize: 12, color: "#A8B0BA" }} />
            <span>Using {pageTitle} as context</span>
            <button
              type="button"
              className="riverr-ask__hint-clear"
              onClick={() => setUsePageContext(false)}
            >
              Clear
            </button>
          </div>
        ) : null}

        <div className="riverr-ask__scroll" ref={listRef}>
          {empty ? (
            <div className="riverr-ask__empty">
              <AskMark size={20} />
              <p className="riverr-ask__empty-title">Ask anything.</p>
              <p className="riverr-ask__empty-sub">
                Your orders, products, stock and payouts — and how the app itself
                works.
              </p>
              {suggestions.length ? (
              <div className="riverr-ask__suggest">
                {suggestions.map((s) => (
                  <button
                    key={s.id || s.q}
                    type="button"
                    className="riverr-ask__suggest-row"
                    onClick={() => {
                      trackAsk("ask_question_sent", {
                        had_page_context: usePageContext,
                      });
                      runQuestion(s.q, { kind: s.kind });
                    }}
                  >
                    <span className="riverr-ask__suggest-q">{s.q}</span>
                    <span className="riverr-ask__chip">{s.tag}</span>
                  </button>
                ))}
              </div>
              ) : null}
              <div className="riverr-ask__sources">
                {Object.entries(ASK_SRC).map(([key, src]) => {
                  const Icon = SRC_ICONS[key];
                  return (
                    <div key={key} className="riverr-ask__source-line">
                      <Icon sx={{ fontSize: 12, color: "#A8B0BA" }} />
                      <span className="riverr-ask__source-tag">{src.label}</span>
                      <span>{src.hint}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              {items.map((item, i) => {
                if (item.role === "user") {
                  return (
                    <div key={item.id} className="riverr-ask__user">
                      <div className="riverr-ask__user-pill">{item.text}</div>
                    </div>
                  );
                }
                if (item.role === "system") {
                  return (
                    <div key={item.id} className="riverr-ask__turn">
                      <p className="riverr-ask__body">
                        <AskRichText>{item.text}</AskRichText>
                      </p>
                    </div>
                  );
                }
                return (
                  <AssistantTurn
                    key={item.id}
                    msg={item}
                    isLast={i === items.length - 1 && !working}
                    isPlatformAdmin={!!isPlatformAdmin}
                    onAction={runAction}
                    onRow={(row) => {
                      trackAsk("ask_row_clicked");
                      goHref(row.href);
                    }}
                    onDoc={(doc) => {
                      if (/^https?:\/\//i.test(doc.url)) {
                        window.open(doc.url, "_blank", "noopener,noreferrer");
                      } else {
                        window.open(doc.url, "_blank", "noopener,noreferrer");
                      }
                    }}
                    onCopy={() => {
                      navigator.clipboard?.writeText((item.body || []).join("\n\n"));
                    }}
                    onRetry={() => runQuestion(lastQuestionRef.current)}
                  />
                );
              })}
              {working ? (
                liveText && !steps.length ? (
                  <div className="riverr-ask__turn">
                    <div className="riverr-ask__turn-row">
                      <div className="riverr-ask__gutter">
                        <AskMark size={13} color="#A8B0BA" />
                      </div>
                      <p className="riverr-ask__body">
                        <AskRichText>{scrubAskText(liveText)}</AskRichText>
                      </p>
                    </div>
                  </div>
                ) : (
                  <Working
                    steps={
                      steps.length
                        ? steps
                        : [{ src: "shop", label: "Searching your shop" }]
                    }
                  />
                )
              ) : null}
              {error ? (
                <div className="riverr-ask__turn">
                  <p className="riverr-ask__body">
                    <AskRichText>{error}</AskRichText>
                  </p>
                  <div className="riverr-ask__actions">
                    <button
                      type="button"
                      className="riverr-ask__action riverr-ask__action--primary"
                      onClick={() => runQuestion(lastQuestionRef.current)}
                    >
                      Retry
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="riverr-ask__composer-wrap">
          <div
            className={`riverr-ask__composer${
              focused ? " riverr-ask__composer--focus" : ""
            }`}
          >
            <textarea
              ref={composerRef}
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  closeAsk();
                  return;
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask anything"
              disabled={working}
            />
            <button
              type="button"
              className={`riverr-ask__send${
                draft.trim() && !working ? " riverr-ask__send--ready" : ""
              }`}
              disabled={!draft.trim() || working}
              aria-label="Send"
              onClick={send}
            >
              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
          <p className="riverr-ask__footnote">
            Ask reads your shop in real time. Actions confirm before they run.
          </p>
        </div>

        {confirm ? (
          <div className="riverr-ask__confirm">
            <div className="riverr-ask__confirm-card" role="alertdialog">
              <p className="riverr-ask__confirm-text">
                {confirm.confirm ||
                  `This will change records. Continue with “${confirm.label}”?`}
              </p>
              <div className="riverr-ask__actions">
                <button
                  type="button"
                  className="riverr-ask__action riverr-ask__action--primary"
                  onClick={confirmMutate}
                >
                  Confirm
                </button>
                <button
                  type="button"
                  className="riverr-ask__action riverr-ask__action--secondary"
                  onClick={() => setConfirm(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="riverr-ask__live" aria-live="polite">
          {liveText}
        </div>
      </aside>
    </>
  );
};

export default AskPanel;
